import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import style from "./VendingMapping.module.css";
import MachineCard from "./MachineCard";
import { requester } from 'App';
import CustomButton from 'components/buttons/CustomButton';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Container from 'layouts/container/Container';
import Center from 'layouts/wrapper/Center';
import P from 'components/P';

const VendingMapping = () => {
    const { t } = useTranslation();
    const { storeId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const storeName = location.state?.storeName || '선택된 매장';

    // 컬럼 상태 관리
    const [columns, setColumns] = useState({
        unregistered: {
            title: "미등록 기기",
            items: []
        },
        vendingMachines: {
            title: "자판기",
            items: []
        },
        kioskMachines: {
            title: "키오스크",
            items: []
        }
    });

    const [loading, setLoading] = useState(true);

    // 모든 기기 정보 로드
    useEffect(() => {
        requester.findAllVending((result) => {
            if (result.data) {
                const machines = result.data;

                // 미등록 기기와 이미 해당 매장에 등록된 기기 분류
                const unregisteredMachines = machines.filter(m => m.storeId === null);
                const registeredMachines = machines.filter(m => m.storeId === storeId);

                // 키오스크와 자판기로 분류 (기존 orderNo 유지)
                const vendingMachines = registeredMachines.filter(m => m.machineType !== "KIOSK")
                    .sort((a, b) => a.orderNo - b.orderNo);
                const kioskMachines = registeredMachines.filter(m => m.machineType === "KIOSK")
                    .sort((a, b) => a.orderNo - b.orderNo);

                // 컬럼 상태 업데이트 (기존 orderNo 그대로 사용)
                setColumns({
                    unregistered: {
                        title: "미등록 기기",
                        items: unregisteredMachines.sort((a, b) => a.orderNo - b.orderNo)
                    },
                    vendingMachines: {
                        title: "자판기",
                        items: vendingMachines
                    },
                    kioskMachines: {
                        title: "키오스크",
                        items: kioskMachines
                    }
                });

                setLoading(false);
            }
        });
    }, [storeId]);

    // 드래그 앤 드롭 처리
    const onDragEnd = (result) => {
        const { source, destination } = result;

        // 드롭이 없거나 같은 위치에 드롭한 경우
        if (!destination ||
            (source.droppableId === destination.droppableId &&
                source.index === destination.index)) {
            return;
        }

        // 소스 및 대상 컬럼과 아이템
        const sourceColumn = columns[source.droppableId];
        const destColumn = columns[destination.droppableId];
        const sourceItems = [...sourceColumn.items];
        const destItems = [...destColumn.items];

        // 소스에서 아이템 제거
        const [removed] = sourceItems.splice(source.index, 1);

        // storeId 업데이트 (미등록이면 null, 아니면 현재 storeId)
        const isMovingToUnregistered = destination.droppableId === "unregistered";
        removed.storeId = isMovingToUnregistered ? null : storeId;

        // 다른 컬럼으로 이동하는 경우
        if (source.droppableId !== destination.droppableId) {
            // 목적지 컬럼에 아이템 추가
            destItems.splice(destination.index, 0, removed);

            // 소스 컬럼의 정렬 (동일 컬럼 내에서만 인덱스 기반으로 정렬)
            const newSourceItems = sourceItems.map((item, index) => ({
                ...item,
                // 컬럼 내 표시 순서만 변경
                orderNo: item.orderNo,
            }));

            // 목적지 컬럼의 정렬 (동일 컬럼 내에서만 인덱스 기반으로 정렬)
            const newDestItems = destItems.map((item, index) => ({
                ...item,
                // 컬럼 내 표시 순서만 변경
                orderNo: item.orderNo,
            }));

            // 상태 업데이트 후 전체 orderNo 재할당
            setColumns(prevColumns => {
                const updatedColumns = {
                    ...prevColumns,
                    [source.droppableId]: {
                        ...sourceColumn,
                        items: newSourceItems,
                    },
                    [destination.droppableId]: {
                        ...destColumn,
                        items: newDestItems,
                    },
                };

                // 전체 등록된 기기 목록 생성 (미등록 기기 제외)
                const allRegisteredMachines = [
                    ...updatedColumns.vendingMachines.items,
                    ...updatedColumns.kioskMachines.items
                ];

                // KIOSK와 일반 기기 분리
                const kioskMachines = allRegisteredMachines.filter(m => m.machineType === "KIOSK");
                const regularMachines = allRegisteredMachines.filter(m => m.machineType !== "KIOSK");

                // 각 그룹 내에서 현재 컬럼 내 순서 유지하면서 전체 orderNo 재할당
                let orderCounter = 1;

                // KIOSK 기기에 먼저 번호 할당 (kioskMachines 컬럼 기준)
                const updatedKioskItems = updatedColumns.kioskMachines.items.map(item => ({
                    ...item,
                    orderNo: orderCounter++
                }));

                // 일반 기기에 나중에 번호 할당 (vendingMachines 컬럼 기준)
                const updatedVendingItems = updatedColumns.vendingMachines.items.map(item => ({
                    ...item,
                    orderNo: orderCounter++
                }));

                // 컬럼 상태 최종 업데이트
                return {
                    ...updatedColumns,
                    vendingMachines: {
                        ...updatedColumns.vendingMachines,
                        items: updatedVendingItems
                    },
                    kioskMachines: {
                        ...updatedColumns.kioskMachines,
                        items: updatedKioskItems
                    }
                };
            });
        } else {
            // 같은 컬럼 내 위치 변경
            sourceItems.splice(destination.index, 0, removed);

            // 같은 컬럼 내에서 인덱스 변경 (orderNo는 아직 변경 안 함)
            const reorderedItems = sourceItems.map(item => ({
                ...item,
                // 이 시점에서는 orderNo를 유지
                orderNo: item.orderNo
            }));

            // 상태 업데이트 후 전체 orderNo 재할당
            setColumns(prevColumns => {
                const updatedColumns = {
                    ...prevColumns,
                    [source.droppableId]: {
                        ...sourceColumn,
                        items: reorderedItems,
                    },
                };

                // 컬럼이 unregistered인 경우 번호 재할당 안 함
                if (source.droppableId === "unregistered") {
                    return updatedColumns;
                }

                // 전체 등록된 기기 목록 생성
                const allRegisteredMachines = [
                    ...updatedColumns.vendingMachines.items,
                    ...updatedColumns.kioskMachines.items
                ];

                // 전체 orderNo 재할당 로직 시작
                let orderCounter = 1;

                // KIOSK 기기에 먼저 번호 할당 (kioskMachines 컬럼 기준)
                const updatedKioskItems = updatedColumns.kioskMachines.items.map(item => ({
                    ...item,
                    orderNo: orderCounter++
                }));

                // 일반 기기에 나중에 번호 할당 (vendingMachines 컬럼 기준)
                const updatedVendingItems = updatedColumns.vendingMachines.items.map(item => ({
                    ...item,
                    orderNo: orderCounter++
                }));

                // 컬럼 상태 최종 업데이트
                return {
                    ...updatedColumns,
                    vendingMachines: {
                        ...updatedColumns.vendingMachines,
                        items: updatedVendingItems
                    },
                    kioskMachines: {
                        ...updatedColumns.kioskMachines,
                        items: updatedKioskItems
                    }
                };
            });
        }
    };


    // 변경사항 저장
    const onUpdateClick = () => {
        // 모든 컬럼의 아이템을 단일 배열로 변환
        const itemsToUpdate = Object.values(columns).flatMap(column =>
            column.items.map(item => ({ ...item }))
        );

        requester.updateVendingList(itemsToUpdate, (result) => {
            if (result.code === 0) {
                toast.success(t("수정되었습니다"), {
                    autoClose: 1000,
                    position: toast.POSITION.BOTTOM_CENTER
                });
            } else {
                toast.error(t("수정 중 오류가 발생했습니다"), {
                    autoClose: 1000,
                    position: toast.POSITION.BOTTOM_CENTER
                });
            }
        });
    }

    // 매장 선택 페이지로 돌아가기
    const handleBackToStores = () => {
        navigate(-1);
    };

    if (loading) {
        return <Center><P>로딩 중...</P></Center>;
    }

    return (
        <Container maxWidth={'100%'}>
            <div className={style.header}>
                <CustomButton
                    text={"← 매장 목록"}
                    onClick={handleBackToStores}
                    style={{ marginRight: 'auto' }}
                />
                <P size={30} font={"gmarketSansBold"}>
                    {storeName} 기기 매핑
                </P>
                <div style={{ width: '120px' }}></div> {/* 균형을 위한 빈 공간 */}
            </div>

            <div className={style.mappingContainer}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className={style.newLayout}>
                        {/* 미등록 섹션 (왼쪽) */}
                        <div className={style.leftSection}>
                            <div className={style.columnHeader}>
                                <span className={style.columnTitle}>{columns.unregistered.title}</span>
                                <span className={style.itemCount}>
                                    ({columns.unregistered.items.length})
                                </span>
                            </div>
                            <Droppable droppableId="unregistered">
                                {(provided, snapshot) => (
                                    <div
                                        className={`${style.columnContent} ${style.unregisteredColumn}`}
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {columns.unregistered.items.map((item, index) => (
                                            <MachineCard
                                                key={item.id}
                                                item={item}
                                                index={index}
                                            />
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>

                        {/* 자판기 & 키오스크 섹션 (오른쪽) */}
                        <div className={style.rightSection}>
                            {/* 자판기 섹션 (위) */}
                            <div className={style.rightTopSection}>
                                <div className={style.columnHeader}>
                                    <span className={style.columnTitle}>{columns.vendingMachines.title}</span>
                                    <span className={style.itemCount}>
                                        ({columns.vendingMachines.items.length})
                                    </span>
                                </div>
                                <Droppable droppableId="vendingMachines">
                                    {(provided, snapshot) => (
                                        <div
                                            className={`${style.columnContent} ${style.vendingColumn}`}
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {columns.vendingMachines.items.map((item, index) => (
                                                <MachineCard
                                                    key={item.id}
                                                    item={item}
                                                    index={index}
                                                />
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>

                            {/* 키오스크 섹션 (아래) */}
                            <div className={style.rightBottomSection}>
                                <div className={style.columnHeader}>
                                    <span className={style.columnTitle}>{columns.kioskMachines.title}</span>
                                    <span className={style.itemCount}>
                                        ({columns.kioskMachines.items.length})
                                    </span>
                                </div>
                                <Droppable droppableId="kioskMachines">
                                    {(provided, snapshot) => (
                                        <div
                                            className={`${style.columnContent} ${style.kioskColumn}`}
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {columns.kioskMachines.items.map((item, index) => (
                                                <MachineCard
                                                    key={item.id}
                                                    item={item}
                                                    index={index}
                                                />
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        </div>
                    </div>
                </DragDropContext>
            </div>

            <div className={style.actionBar}>
                <CustomButton
                    text={"적용"}
                    onClick={onUpdateClick}
                    style={{
                        border: "none",
                        backgroundColor: "var(--main-color)",
                        color: "white",
                        padding: "10px 30px",
                        fontSize: "16px"
                    }}
                />
            </div>
        </Container>
    );
};

export default VendingMapping;